import React, { useEffect, useState, memo, useMemo } from 'react'
import toast from 'react-hot-toast'
import { contentAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import SearchBar from '../components/SearchBar'
import { useForm } from 'react-hook-form'
import MultiImageUpload from '../components/MultiImageUpload'
import MDEditor from '@uiw/react-md-editor';

function AdminMading() {
    const [loading, setLoading] = useState(true)
    const [mading, setMading] = useState(null)
    const [madingType, setMadingType] = useState('data')
    const [judul, setJudul] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [deskripsi, setDeskripsi] = useState('')
    const [editItem, setEditItem] = useState(null)
    const [editImages, setEditImages] = useState([])
    const [editContent, setEditContent] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)
    const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm()

    // Ambil semua data mading
    const fetchMading = async () => {
        setLoading(true)
        try {
            const res = await contentAPI.getAllMadingAdmin()
            setMading(res.data)
            setJudul(res.data.MadingJudul || '')
            setDeskripsi(res.data.MadingDeskipsi || '')
        } catch (error) {
            toast.error('Gagal memuat data mading')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchMading()
    }, [])

    // Update judul & deskripsi mading
    const handleUpdateSection = async () => {
        try {
            await contentAPI.updateMadingSection({ MadingJudul: judul, MadingDeskipsi: deskripsi })
            toast.success('Section mading berhasil diperbarui')
            fetchMading()
        } catch (error) {
            toast.error('Gagal memperbarui section mading')
        }
    }
    const normalize = (val) => String(val || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

    const filterData = (data) => {
        if (!Array.isArray(data)) return [];
        if (!searchTerm.trim()) return data;

    const keywords = normalize(searchTerm).split(" "); // bisa multi kata

        return data.filter(item => {
            if (!item) return false;

            const searchableFields = [
            item.MadingDataJudul,
            item.MadingDataPembuat,
            item.MadingDataIsi,
            item.updatedBy?.name,
            item.updatedBy?.email,
            item.deletedReason
            ].map(normalize);

            // Harus match semua keyword
            return keywords.every(kw =>
            searchableFields.some(field => field.includes(kw))
            );
        });
    };

    // Tambahkan memo untuk hasil filter
    const filteredData = useMemo(() => {
        if (!mading) return []
        const data = madingType === 'data'
            ? mading?.MadingData?.filter(item => !item.deleted) || []
            : mading?.MadingData?.filter(item => item.deleted) || []
        
            // Sort by createdAt date, newest first
    const sortedData = [...filterData(data)].sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt)
    })
    return sortedData
    }, [mading, madingType, searchTerm])

    function escapeRegex(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    const Highlight = ({ text = "", query = "" }) => {
        if (!query) return <>{text}</>;
        const regex = new RegExp(`(${escapeRegex(query)})`, "gi");
        const parts = text.split(regex);

    return (
        <>
        {parts.map((part, i) =>
            part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200">{part}</mark>
            ) : (
            <span key={i}>{part}</span>
            )
        )}
        </>
    );
    };

    // Hapus mading item tertentu dengan alasan
    const handleDeleteItem = async (itemId) => {
            try {
                const reason = window.prompt('Masukkan alasan penghapusan mading ini:')
                if (!reason) {
                    toast.error('Alasan wajib diisi untuk menghapus mading')
                    return
                }
                
                if (!window.confirm('Yakin ingin menghapus item ini?')) {
                    return
                }

                setLoading(true) // Tambah loading state
                
                const response = await contentAPI.deleteMadingItem(itemId, reason)
                
                if (response.data?.message) {
                    toast.success(response.data.message)
                } else {
                    toast.success('Mading berhasil dihapus')
                }
                
                await fetchMading() // Refresh data
            } catch (error) {
                console.error('Delete error:', error)
                toast.error(error?.response?.data?.message || 'Gagal menghapus mading')
            } finally {
                setLoading(false)
            }
        }

    // Update the handleEditItem function
    const handleEditItem = (item) => {
        setEditItem(item)
        setValue('MadingDataJudul', item.MadingDataJudul)
        setValue('MadingDataPembuat', item.MadingDataPembuat)
        setValue('MadingDataTanggal', new Date(item.MadingDataTanggal).toISOString().split('T')[0])
        setEditImages(item.MadingDataImage || [])
        setEditContent(item.MadingDataIsi || '')
    }

    // Update the onEditSubmit function
    const onEditSubmit = async (data) => {
        try {
            setSavingEdit(true)
            
            // Validasi
            if (!editItem || !mading) {
                throw new Error('Invalid edit state')
            }

            if (!data.MadingDataJudul?.trim()) {
                throw new Error('Judul is required')
            }

            // Validate images
            if (editImages.length > 0) {
                const invalidImages = editImages.filter(img => !img.url)
                if (invalidImages.length > 0) {
                    throw new Error('Some images failed to upload')
                }
            }

            // Format data sesuai dengan model
            const updatedItem = {
                ...editItem,
                MadingDataJudul: data.MadingDataJudul,
                MadingDataPembuat: data.MadingDataPembuat, 
                MadingDataTanggal: data.MadingDataTanggal,
                MadingDataImage: editImages,
                MadingDataIsi: editContent,
                updatedBy: editItem.updatedBy
            }

            // Update local state optimistically
            const updatedMadingData = mading.MadingData.map(item => 
                item._id === editItem._id ? updatedItemadm : item
            )

            const updatedMading = {
                ...mading,
                MadingData: updatedMadingData
            }

            setMading(updatedMading)

            // Send update to backend
            await contentAPI.updateAdminMading(mading._id, {
                MadingJudul: mading.MadingJudul,
                MadingDeskipsi: mading.MadingDeskipsi,
                MadingData: updatedMadingData
            })

            toast.success('Item berhasil diupdate')
            
            // Reset form state
            setEditItem(null)
            setEditImages([])
            setEditContent('')
            reset()
            
            // Refresh data
            await fetchMading()
        } catch (err) {
            console.error('Update error:', err)
            // Revert optimistic update on error
            await fetchMading()
            toast.error(err.message || 'Gagal update item')
        } finally {
            setSavingEdit(false)
        }
    }

    // handler untuk recovery
    const handleRecoveryItem = async (itemId) => {
        try {
            if (!window.confirm('Yakin ingin memulihkan item ini?')) {
                return;
            }
            setLoading(true);
            await contentAPI.recoveryMadingItem(itemId);
            toast.success('Mading berhasil dipulihkan');
            await fetchMading();
        } catch (error) {
            console.error('Recovery error:', error);
            toast.error(error?.response?.data?.message || 'Gagal memulihkan mading');
        } finally {
            setLoading(false);
        }
    }


    if (loading) return <LoadingSpinner size="lg" />

    return (
        <div className='space-y-6'>
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="header-page">Admin Mading</h1>
                    <p className="description-page">Halaman ini digunakan admin untuk mengelola mading.</p>
                </div>
                <button
                    onClick={handleUpdateSection}
                    className="btn btn-primary"
                >
                    Simpan Perubahan
                </button>
            </div>

            <form className="space-y-8">
                <div className='card'>
                    <div className="card-header">
                        <h3 className="card-title">Edit Mading</h3>
                        <p className="card-description">Semua kontrol mading ada di sini</p>
                    </div>
                    <div className='card-content space-y-4'>
                        <div>
                            <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                                Section Title (opsional)
                            </label>
                            <input
                                type="text"
                                value={judul}
                                onChange={(e) => setJudul(e.target.value)}
                                className="input"
                            />
                        </div>
                        <div>
                            <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-2">
                                Section Description (opsional)
                            </label>
                            <input
                                type="text"
                                value={deskripsi}
                                onChange={(e) => setDeskripsi(e.target.value)}
                                className="input"
                            />
                        </div>
                        
                        {/* Tombol Data Mading dan Recovery Mading dengan animasi */}
                        <div className="flex justify-center gap-4 mb-4">
                            <button
                            type='button'
                                className={`lg:px-4 lg:py-2 px-2 py-2 lg:text-sm text-xs rounded-lg transition-all duration-200 ease-in-out transform ${
                                    madingType === 'data'
                                        ? 'bg-teal-600 text-white scale-105 shadow-lg'
                                        : 'bg-gray-200 text-gray-700 hover:scale-105 hover:shadow'
                                }`}
                                onClick={(e) => { e.preventDefault(); setMadingType('data'); }}
                            >
                                Daftar Mading
                            </button>
                            <button
                            type='button'
                                className={`lg:px-4 lg:py-2 px-2 py-2 lg:text-sm text-xs rounded-lg transition-all duration-200 ease-in-out transform ${
                                    madingType === 'recovery'
                                        ? 'bg-teal-600 text-white scale-105 shadow-lg'
                                        : 'bg-gray-200 text-gray-700 hover:scale-105 hover:shadow'
                                }`}
                                onClick={(e) => { e.preventDefault(); setMadingType('recovery'); }}
                            >
                                Recovery Mading
                            </button>
                        </div>

                        {/* Fitur find */}
                        <div className="mb-4 gap-3 align-center">
                            <SearchBar
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                resultCount={filteredData.length}
                            />
                        </div>
                        
                        <div className="overflow-x-auto">
                            { madingType === 'data' ? (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Judul</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pembuat</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dibuat</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredData.map((item) => (
                                            <tr key={item._id}>
                                                {/* highlight */}
                                                <td className="px-6 py-4 whitespace-wrap text-xs"><Highlight text={item.MadingDataJudul} query={searchTerm} /></td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs"><Highlight text={item.MadingDataPembuat} query={searchTerm} /></td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">{item.MadingDataTanggal?.slice(0,10)} </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                                        item.publicationStatus === 'published' 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                        {item.publicationStatus === 'published' ? 'Terpublikasi' : 'Draft'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">
                                                    {item.createdAt ? new Date(item.createdAt).toLocaleString('id-ID', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit'
                                                    }) : '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">{item.updatedBy?.name || '-'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">{item.updatedBy?.email || '-'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">
                                                    {/* Fitur edit admin coming soon dev lagi sibuk gak sempet */}
                                                    {/* <button className="btn btn-secondary" onClick={() => handleEditItem(item)}>Edit</button> */}
                                                    <button
                                                        className="btn btn-danger ml-2"
                                                        onClick={() => handleDeleteItem(item._id)}
                                                        disabled={loading}
                                                    >
                                                        Hapus
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead>
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Judul</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pembuat</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal Hapus</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alasan</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredData.map((item) => (
                                            <tr key={item._id}>
                                                <td className="px-6 py-4 whitespace-wrap text-xs">{item.MadingDataJudul}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">{item.MadingDataPembuat}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">
                                                    {item.deletedAt ? new Date(item.deletedAt).toLocaleString('id-ID', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit'
                                                    }) : '-'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">
                                                    <span className='px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800'>{item.deletedReason}</span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">{item.updatedBy?.name || '-'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">{item.updatedBy?.email || '-'}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs">
                                                    <button
                                                        className="btn btn-primary"
                                                        onClick={() => handleRecoveryItem(item._id)}
                                                        disabled={loading}
                                                    >
                                                        Pulihkan
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </form>
            {/* 
            
            
            {editItem && (
                <div className="card">
                    <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4 bg-white p-4 rounded shadow">
                        <h3 className="text-lg font-semibold mb-4">Edit Mading</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Judul</label>
                                <input 
                                    {...register('MadingDataJudul', { required: true })} 
                                    className="input w-full"
                                    placeholder="Judul" 
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pembuat</label>
                                <input 
                                    {...register('MadingDataPembuat', { required: true })} 
                                    className="input w-full"
                                    placeholder="Pembuat" 
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal</label>
                                <input 
                                    type="date" 
                                    {...register('MadingDataTanggal', { required: true })} 
                                    className="input w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Gambar</label>
                                <MultiImageUpload
                                    images={editImages}
                                    onUpload={img => setEditImages(prev => [...prev, img])}
                                    onRemove={idx => setEditImages(prev => prev.filter((_, i) => i !== idx))}
                                    maxFiles={10}
                                    maxSize={5 * 1024 * 1024} // 5MB
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Isi Konten</label>
                                <MDEditor
                                    value={editContent}
                                    onChange={setEditContent}
                                    preview="edit"
                                    height={400}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <button 
                                type="submit" 
                                className="btn btn-primary" 
                                disabled={savingEdit}
                            >
                                {savingEdit ? <LoadingSpinner size="sm" /> : "Simpan"}
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={() => {
                                    setEditItem(null)
                                    setEditImages([])
                                    setEditContent('')
                                }} 
                                disabled={savingEdit}
                            >
                                Batal
                            </button>
                        </div>
                    </form>
                </div>
            )}
                */}
        </div>
    )
}

export default memo(AdminMading)